import { ResizeSensor } from '@blueprintjs/core';
import type { ResizeObserverEntry } from '@juggle/resize-observer';
import classNames from 'classnames';
import * as React from 'react';
import { MosaicWithoutDragDropContext } from 'react-mosaic-component';
import { MosaicKey, MosaicNode } from 'react-mosaic-component/lib/types';
import { isEqual, noop } from 'vet-bones/utils';

import {
  MosaicCollapseOptions,
  MosaicCollapseOptionsById,
  MosaicCollapsedById,
  MosaicViewManagerProps,
} from 'src/components/mosaic/types';
import { NestedMosaicParent } from 'src/constants/mosaic';
import { getMosaicTestId } from 'src/constants/testIds';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import {
  determineCollapsedMosaicValue,
  determineInitialMosaicValue,
  determineOpenedMosaicValue,
  determineUpdatedMosaicValue,
  getComponentMap,
} from 'src/utils/mosaic';

const getCollapsedById = <T extends MosaicKey>(
  collapseOptions: MosaicCollapseOptionsById<T>
) =>
  Object.entries<MosaicCollapseOptions | undefined>(collapseOptions).reduce<
    MosaicCollapsedById<T>
  >((acc, [id, options]) => {
    if (options) {
      acc[id as T] = options.collapsed;
    }
    return acc;
  }, {} as any);

export const MosaicViewManager: React.VFC<MosaicViewManagerProps> = ({
  children,
  collapseOptions,
  containerClassName,
  initialValue,
  onToggleCollapsed = noop,
  resizable = true,
  resizeOptions = {
    minimumPaneSizePercentage: 1, // just in case, let's not totally hide sibling tiles
  },
  splitOptions,
  showSplitLine = true,
}) => {
  const [value, setValue] =
    React.useState<NestedMosaicParent<MosaicKey> | null>(null);
  const [containerSize, setContainerSize] = React.useState<{
    width: number;
    height: number;
  } | null>(null);
  const [collapsedById, setCollapsedById] = React.useState<
    MosaicCollapsedById<MosaicKey>
  >(getCollapsedById(collapseOptions));

  const didCollapseOptionsChange = useDidValueChange(collapseOptions);
  const didSplitOptionsChange = useDidValueChange(splitOptions, isEqual);

  const handleMosaicChange = React.useCallback(
    (node: MosaicNode<MosaicKey> | null) => {
      if (!node) {
        return;
      }

      const initialChange = node as NestedMosaicParent<MosaicKey>;
      let newCollapsedById = collapsedById;
      const setIdCollapsed = (id: MosaicKey, value: boolean) => {
        if (collapseOptions[id] && newCollapsedById[id] !== value) {
          newCollapsedById = { ...newCollapsedById, [id]: value };
        }
      };
      const finalChange = determineUpdatedMosaicValue(
        containerSize,
        initialChange,
        collapseOptions,
        collapsedById,
        splitOptions,
        setIdCollapsed
      );
      setValue(finalChange);
      setCollapsedById(newCollapsedById);
    },
    [collapsedById, collapseOptions, containerSize, splitOptions]
  );

  const handleResize = React.useCallback(
    (entries: ResizeObserverEntry[]) => {
      const { height, width } = entries[0].contentRect;
      setContainerSize({ height, width });
      if (!value) {
        const newInitialValue = determineInitialMosaicValue(
          { height, width },
          initialValue,
          splitOptions
        );
        handleMosaicChange(newInitialValue);
      } else {
        handleMosaicChange(value);
      }
    },
    [handleMosaicChange, initialValue, splitOptions, value]
  );

  React.useEffect(() => {
    if (didSplitOptionsChange && value) {
      handleMosaicChange(value);
    }
  }, [didSplitOptionsChange, handleMosaicChange, value]);

  React.useEffect(() => {
    if (didCollapseOptionsChange) {
      const newCollapsedById = { ...collapsedById };
      Object.entries<MosaicCollapseOptions | undefined>(
        collapseOptions
      ).forEach(([id, options]) => {
        if (options) {
          newCollapsedById[id] = options.collapsed;
        }
      });
      if (containerSize && value) {
        const newValue = determineOpenedMosaicValue(
          value,
          splitOptions,
          newCollapsedById,
          collapsedById
        );
        handleMosaicChange(newValue);
      }
      setCollapsedById(newCollapsedById);
    }
  }, [
    collapsedById,
    collapseOptions,
    containerSize,
    didCollapseOptionsChange,
    handleMosaicChange,
    initialValue,
    splitOptions,
    value,
  ]);

  const componentMap = React.useMemo(
    () => getComponentMap(children, collapsedById, onToggleCollapsed),
    [children, collapsedById, onToggleCollapsed]
  );

  const collapsedValue = React.useMemo(
    () =>
      value && containerSize
        ? determineCollapsedMosaicValue(
            containerSize,
            value,
            collapseOptions,
            collapsedById
          )
        : value,

    [containerSize, value, collapsedById, collapseOptions]
  );

  return (
    <ResizeSensor onResize={handleResize}>
      <MosaicWithoutDragDropContext
        className={classNames(containerClassName, {
          'mosaic--show-split-line': showSplitLine,
        })}
        onChange={handleMosaicChange}
        renderTile={(id: MosaicKey) => (
          <div
            className={classNames('mosaic-tile-contents', {
              'mosaic-tile-contents--hidden':
                collapsedById[id] && !collapseOptions[id]?.collapsedVisible,
            })}
            data-testid={getMosaicTestId(id)}
          >
            {componentMap[id]}
          </div>
        )}
        resize={
          !resizable
            ? 'DISABLED'
            : resizeOptions || {
                minimumPaneSizePercentage: 1, // just in case, let's not totally hide sibling tiles
              }
        }
        value={collapsedValue}
      />
    </ResizeSensor>
  );
};
